import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "@/redux/theme";
import {
    RobotOutlined,
    SendOutlined,
    DeleteOutlined,
    UploadOutlined,
    AlertOutlined
} from '@ant-design/icons';

// axios defaults baseURL is already /api/ in this project
const API_BASE = "ai-chat";

const SUGGESTION_BUTTONS = [
    { label: "📋 Leave Policy", query: "What is the company leave policy?" },
    { label: "💰 Salary Date", query: "When is the salary credited?" },
    { label: "⏰ Attendance Rules", query: "What are the attendance and late policy rules?" },
    { label: "📝 Probation Period", query: "What are the probation rules?" },
    { label: "🚪 Resignation Policy", query: "What is the resignation and notice period policy?" },
    { label: "🎯 Benefits", query: "What employee benefits does the company offer?" },
];

export default function CompanyChatbot({ userRole }) {
    const isDarkMode = useSelector(selectIsDarkMode);
    const [messages, setMessages] = useState([
        { role: "assistant", content: "👋 Hi! I'm your HR Support Assistant. I can answer questions about company policies based on the Employee Handbook.\n\nTry one of the quick buttons below, or ask me anything!" }
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [docLoaded, setDocLoaded] = useState(false);
    const [docCount, setDocCount] = useState(0);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem("hr_chatbot_session_id"));
    const [canUpload, setCanUpload] = useState(false);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [uploadCategory, setUploadCategory] = useState("General");
    const [uploadTitle, setUploadTitle] = useState("");
    const bottomRef = useRef(null);
    const fileRef = useRef(null);

    const CATEGORIES = [
        "Employee Handbook",
        "IT & Security Policy",
        "General Policy",
        "Other"
    ];

    // Theme values
    const colors = {
        bg: isDarkMode ? "#0d1117" : "#ffffff",
        headerBg: isDarkMode ? "#161b22" : "#f6f8fa",
        border: isDarkMode ? "#30363d" : "#d0d7de",
        text: isDarkMode ? "#e6edf3" : "#24292f",
        subText: isDarkMode ? "#8b949e" : "#6e7781",
        userMsgBg: "linear-gradient(135deg, #1890ff, #096dd9)",
        botMsgBg: isDarkMode ? "#21262d" : "#f0f2f5",
        inputBg: isDarkMode ? "#0d1117" : "#f6f8fa",
        btnBg: isDarkMode ? "#21262d" : "#ffffff",
    };

    // Check status and LOAD HISTORY on mount
    useEffect(() => {
        axios.get(`${API_BASE}/status`)
            .then(res => {
                const data = res.data;
                if (data.hasDocument) {
                    setDocLoaded(true);
                    setDocCount(data.documentCount);
                }
                const role = data.userRole || userRole;
                const hrRoles = ['super_admin', 'hr_admin', 'Admin', 'department_admin'];
                setCanUpload(hrRoles.includes(role));
            })
            .catch(() => { });

        // Load history if sessionId exists
        if (sessionId) {
            axios.get(`${API_BASE}/history?sessionId=${sessionId}`)
                .then(res => {
                    if (res.data.success && res.data.messages?.length > 0) {
                        setMessages(res.data.messages);
                    }
                })
                .catch(() => { });
        }
    }, []);

    // Effect to update localStorage when sessionId changes
    useEffect(() => {
        if (sessionId) {
            localStorage.setItem("hr_chatbot_session_id", sessionId);
        } else {
            localStorage.removeItem("hr_chatbot_session_id");
        }
    }, [sessionId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // ─── CLEAR CHAT ─────────────────────────────────────────────────────
    const clearChat = () => {
        setMessages([{ role: "assistant", content: "👋 Chat history cleared. How can I help you today?" }]);
        setSessionId(null);
        localStorage.removeItem("hr_chatbot_session_id");
    };

    // ─── Send Message ───────────────────────────────────────────────────
    const sendMessage = async (msgText) => {
        const text = msgText || input.trim();
        if (!text || loading) return;

        const userMsg = { role: "user", content: text };
        const newMessages = [...messages, userMsg];
        setMessages(newMessages);
        setInput("");
        setLoading(true);

        try {
            const history = newMessages.slice(-10, -1);
            const res = await axios.post(API_BASE, {
                message: text,
                history,
                sessionId,
            });

            const data = res.data;
            if (data.sessionId) setSessionId(data.sessionId);

            setMessages(prev => [...prev, {
                role: "assistant",
                content: data.reply || data.message || "No response received."
            }]);
        } catch (err) {
            const errMsg = err.response?.data?.message || "Server connection error.";
            setMessages(prev => [...prev, { role: "assistant", content: `⚠️ ${errMsg}` }]);
        }

        setLoading(false);
    };

    // ─── Upload File ────────────────────────────────────────────────────
    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const form = new FormData();
        form.append("document", file);
        form.append("category", uploadCategory);
        form.append("title", uploadTitle || file.name);

        try {
            const res = await axios.post(`${API_BASE}/upload`, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const data = res.data;

            if (data.success) {
                setDocLoaded(true);
                setDocCount(prev => prev + 1);
                setShowUploadForm(false);
                setUploadTitle("");
                setMessages(prev => [...prev, {
                    role: "assistant",
                    content: `✅ "${data.entry.title}" uploaded to **${data.entry.category}** (${data.entry.characters.toLocaleString()} characters). The handbook is now updated!`
                }]);
            } else {
                setMessages(prev => [...prev, { role: "assistant", content: `❌ Upload failed: ${data.message}` }]);
            }
        } catch (err) {
            const errMsg = err.response?.data?.message || "Could not connect to server.";
            setMessages(prev => [...prev, { role: "assistant", content: `❌ ${errMsg}` }]);
        }

        setUploading(false);
        e.target.value = "";
    };

    // ─── Escalate to HR ─────────────────────────────────────────────────
    const handleEscalate = async () => {
        if (!sessionId) {
            setMessages(prev => [...prev, { role: "assistant", content: "Start a conversation first before escalating." }]);
            return;
        }

        try {
            const res = await axios.post(`${API_BASE}/escalate`, {
                sessionId,
                note: "Employee requested HR assistance via chatbot"
            });

            setMessages(prev => [...prev, {
                role: "assistant",
                content: "📩 " + (res.data.message || "Your query has been escalated to HR.")
            }]);
        } catch (err) {
            setMessages(prev => [...prev, { role: "assistant", content: "⚠️ Failed to escalate. Please try again." }]);
        }
    };

    // ─── Formatting Helper ──────────────────────────────────────────────
    const renderContent = (content) => {
        if (!content) return null;

        // Split by lines to handle bullet points
        const lines = content.split("\n");
        return lines.map((line, idx) => {
            let formattedLine = line;
            const isBullet = line.trim().startsWith("* ");

            if (isBullet) {
                formattedLine = line.trim().substring(2);
            }

            // Handle Bold (**text**)
            const parts = formattedLine.split(/(\*\*.*?\*\*)/g);
            const renderedLine = parts.map((part, pidx) => {
                if (part.startsWith("**") && part.endsWith("**")) {
                    return <strong key={pidx} style={{ color: isDarkMode ? "#fff" : "inherit" }}>{part.slice(2, -2)}</strong>;
                }
                return part;
            });

            return (
                <div key={idx} style={{
                    marginBottom: idx === lines.length - 1 ? 0 : "4px",
                    display: isBullet ? "list-item" : "block",
                    listStyleType: isBullet ? "disc" : "none",
                    marginLeft: isBullet ? "20px" : "0"
                }}>
                    {renderedLine}
                </div>
            );
        });
    };

    return (
        <div style={{
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            display: "flex", flexDirection: "column", height: "100%", width: "100%",
            background: colors.bg, color: colors.text, borderRadius: "12px", overflow: "hidden"
        }}>

            {/* ── Header ── */}
            <div style={{
                padding: "14px 20px", borderBottom: `1px solid ${colors.border}`,
                background: colors.headerBg, display: "flex", alignItems: "center",
                justifyContent: "space-between", gap: 10, flexWrap: "nowrap"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: "linear-gradient(135deg, #1890ff, #3ecfcf)",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0
                    }}><RobotOutlined /></div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>HR Support Assistant</div>
                        <div style={{ fontSize: 10, color: colors.subText }}>
                            {docLoaded ? `${docCount} doc${docCount > 1 ? 's' : ''}` : 'No docs'}
                        </div>
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                    <button
                        onClick={clearChat}
                        title="Clear History"
                        style={{
                            background: colors.btnBg,
                            border: `1px solid ${colors.border}`, color: colors.subText, width: 32, height: 32,
                            borderRadius: 8, cursor: "pointer", display: "flex",
                            alignItems: "center", justifyContent: "center", fontSize: 14,
                            transition: "all 0.2s"
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = isDarkMode ? "#30363d" : "#f3f4f6"; e.currentTarget.style.color = colors.text; }}
                        onMouseLeave={e => { e.currentTarget.style.background = colors.btnBg; e.currentTarget.style.color = colors.subText; }}
                    ><DeleteOutlined /></button>

                    {canUpload && (
                        <button
                            onClick={() => setShowUploadForm(!showUploadForm)}
                            style={{
                                background: "linear-gradient(135deg, #1890ff, #3ecfcf)",
                                border: "none", color: "#fff", padding: "6px 12px",
                                borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 11,
                                display: "flex", alignItems: "center", gap: 4
                            }}>
                            <UploadOutlined /> Upload
                        </button>
                    )}
                </div>
            </div>

            {/* ── Upload Form (HR/Admin only) ── */}
            {showUploadForm && canUpload && (
                <div style={{
                    padding: "12px 20px", background: colors.headerBg, borderBottom: `1px solid ${colors.border}`,
                    display: "flex", flexDirection: "column", gap: 8
                }}>
                    <input
                        value={uploadTitle}
                        onChange={e => setUploadTitle(e.target.value)}
                        placeholder="Document title (optional)"
                        style={{
                            background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6,
                            padding: "8px 12px", color: colors.text, fontSize: 12, outline: "none"
                        }}
                    />
                    <select
                        value={uploadCategory}
                        onChange={e => setUploadCategory(e.target.value)}
                        style={{
                            background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6,
                            padding: "8px 12px", color: colors.text, fontSize: 12, outline: "none"
                        }}
                    >
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button
                        onClick={() => fileRef.current.click()}
                        disabled={uploading}
                        style={{
                            background: "#1890ff", border: "none", color: "#fff", padding: "8px 14px",
                            borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12,
                            opacity: uploading ? 0.6 : 1
                        }}>
                        {uploading ? "Uploading..." : "Choose File (PDF, TXT, MD)"}
                    </button>
                    <input ref={fileRef} type="file" accept=".pdf,.txt,.md" onChange={handleUpload} style={{ display: "none" }} />
                </div>
            )}

            {/* ── Messages ── */}
            <div style={{ flex: 1, overflowY: "auto", padding: "15px", display: "flex", flexDirection: "column", gap: 12 }}>
                {messages.map((msg, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 8 }}>
                        {msg.role === "assistant" && (
                            <div style={{
                                width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                                background: "linear-gradient(135deg, #1890ff, #3ecfcf)",
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#fff"
                            }}><RobotOutlined /></div>
                        )}
                        <div style={{
                            maxWidth: "85%", padding: "10px 14px",
                            borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                            background: msg.role === "user" ? colors.userMsgBg : colors.botMsgBg,
                            border: msg.role === "user" ? "none" : `1px solid ${colors.border}`,
                            fontSize: 13, lineHeight: 1.5,
                            color: msg.role === "user" ? "#fff" : colors.text,
                        }}>
                            {renderContent(msg.content)}
                        </div>
                    </div>
                ))}

                {loading && (
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                        <div style={{
                            width: 24, height: 24, borderRadius: 6,
                            background: "linear-gradient(135deg, #1890ff, #3ecfcf)",
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#fff"
                        }}><RobotOutlined /></div>
                        <div style={{
                            padding: "10px 16px", background: colors.botMsgBg, border: `1px solid ${colors.border}`,
                            borderRadius: "16px 16px 16px 4px", color: colors.subText, fontSize: 13
                        }}>
                            Thinking...
                        </div>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {/* ── Quick Suggestion Buttons ── */}
            {messages.length <= 2 && docLoaded && (
                <div style={{
                    padding: "8px 15px", display: "flex", flexWrap: "wrap", gap: 6,
                    borderTop: `1px solid ${colors.border}`, background: colors.headerBg
                }}>
                    {SUGGESTION_BUTTONS.map((btn, i) => (
                        <button
                            key={i}
                            onClick={() => sendMessage(btn.query)}
                            disabled={loading}
                            style={{
                                background: colors.btnBg, border: `1px solid ${colors.border}`,
                                borderRadius: 20, padding: "6px 12px",
                                color: colors.subText, cursor: "pointer", fontSize: 11,
                                transition: "all 0.2s",
                                whiteSpace: "nowrap"
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = isDarkMode ? "#30363d" : "#f3f4f6"; e.currentTarget.style.color = colors.text; }}
                            onMouseLeave={e => { e.currentTarget.style.background = colors.btnBg; e.currentTarget.style.color = colors.subText; }}
                        >
                            {btn.label}
                        </button>
                    ))}
                </div>
            )}

            {/* ── Input + Escalate ── */}
            <div style={{
                padding: "12px 15px", borderTop: `1px solid ${colors.border}`,
                background: colors.headerBg, display: "flex", gap: 8, alignItems: "center"
            }}>
                <button
                    onClick={handleEscalate}
                    title="Escalate to HR"
                    style={{
                        background: isDarkMode ? "#2a1a1a" : "#fff1f0", border: `1px solid ${isDarkMode ? "#5a2020" : "#ffa39e"}`,
                        borderRadius: 8, padding: "10px", cursor: "pointer",
                        color: "#ff4d4f", fontSize: 14, flexShrink: 0,
                        transition: "all 0.2s",
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = isDarkMode ? "#3a2020" : "#ffccc7"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isDarkMode ? "#2a1a1a" : "#fff1f0"; }}
                ><AlertOutlined /></button>

                <input
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && sendMessage()}
                    placeholder={docLoaded ? "Ask about company policies..." : "Waiting for HR to upload handbook..."}
                    disabled={!docLoaded || loading}
                    style={{
                        flex: 1, background: colors.inputBg, border: `1px solid ${colors.border}`,
                        borderRadius: 8, padding: "10px 14px", color: colors.text,
                        fontSize: 13, outline: "none",
                        opacity: !docLoaded ? 0.5 : 1
                    }}
                />
                <button
                    onClick={() => sendMessage()}
                    disabled={!docLoaded || loading || !input.trim()}
                    style={{
                        background: "linear-gradient(135deg, #1890ff, #3ecfcf)",
                        border: "none", borderRadius: 8, padding: "10px 14px",
                        color: "#fff", cursor: "pointer", fontSize: 16,
                        opacity: (!docLoaded || loading || !input.trim()) ? 0.4 : 1,
                        transition: "opacity 0.2s",
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}><SendOutlined /></button>
            </div>

            <style>{`
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: ${colors.bg}; }
        ::-webkit-scrollbar-thumb { background: ${isDarkMode ? "#30363d" : "#d0d7de"}; border-radius: 2px; }
      `}</style>
        </div>
    );
}
